import { Key } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';
import { ChipListSkeleton } from './ChipListSkeleton';

type ChipInfo = {
  id: Key;
  primary: string;
  secondary: string;
};

type ChipListProps = {
  chips: ChipInfo[];
  isLoading: boolean;
  emptyMessage: string;
  totalCount?: number;
  visibleChipsCount?: number;
};

export const ChipList = ({
  chips,
  isLoading,
  emptyMessage,
  totalCount = chips.length,
  visibleChipsCount = 3,
}: ChipListProps) => {
  const intl = useIntl();

  if (isLoading) {
    return <ChipListSkeleton />;
  }

  if (!chips.length) {
    return (
      <Stack alignItems="flex-start">
        <Chip
          label={
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          }
        />
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1}>
      {chips.slice(0, visibleChipsCount).map(({ id, primary, secondary }) => (
        <Tooltip key={id} title={`${primary} - ${secondary}`} arrow>
          <Chip
            label={
              <Stack
                direction="row"
                spacing={0}
                alignItems="center"
                divider={<>-</>}
                gap={0.5}
                sx={{
                  '& p': {
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                <Typography variant="inherit">{primary}</Typography>
                <Typography variant="inherit" fontWeight="bold">
                  {secondary}
                </Typography>
              </Stack>
            }
            sx={{
              maxWidth: 250,
            }}
          />
        </Tooltip>
      ))}

      {chips.length > visibleChipsCount && (
        <Tooltip
          arrow
          title={
            <Stack spacing={1} alignItems="flex-start">
              {chips.slice(visibleChipsCount).map(info => (
                <Box
                  key={`${info.id}`}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="caption">
                    {info.primary}
                    <Box component="span" mx={0.5}>
                      -
                    </Box>
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {info.secondary}
                  </Typography>
                </Box>
              ))}

              {totalCount - chips.length > 0 ? (
                <Typography
                  component="div"
                  variant="caption"
                  align="left"
                  sx={{ pt: 1 }}
                >
                  {intl.formatMessage(
                    {
                      id: 'components.engineCardQueues.more',
                    },
                    {
                      count: totalCount - chips.length,
                    }
                  )}
                </Typography>
              ) : null}
            </Stack>
          }
        >
          <Chip
            label={
              <Typography variant="caption">
                +{`${totalCount - visibleChipsCount}`}
              </Typography>
            }
          />
        </Tooltip>
      )}
    </Stack>
  );
};
